/* Employees, documents, files. */
import { allowancesFor, validateEmployee } from "../../domain/compliance";
import { isISODate } from "../../domain/dates";
import type * as T from "../types";
import { E, has, isInt, sanitizeName, str, type Ctx } from "./ctx";
import { expiringList, putFile } from "./derived";

export const FOLDERS = ["receipts", "documents", "expenses", "leave", "exports", "letters", "imports", "photos", "biometric"];
export const FILE_TYPES: Record<string, string> = {
  pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", heic: "image/heic",
  csv: "text/csv", txt: "text/plain", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", xls: "application/vnd.ms-excel",
};
export const MAX_UPLOAD = 10 << 20;

type Row = Record<string, unknown>;

function joinFields(fe: Record<string, string>) {
  return Object.entries(fe).map(([k, v]) => k + ": " + v).join("; ");
}

/** Validate + apply server defaults (no store write). Returns field errors or the employee (id/code unset). */
function buildEmployee(c: Ctx, input: Row): { fe: Record<string, string> } | { e: T.Employee } {
  const m: Row = { ...input };
  for (const k of ["id", "code", "qiwa", "gosiWage", "photoKey"]) delete m[k];
  if (typeof m.idNumber === "string") m.idNumber = m.idNumber.trim();
  const fe = validateEmployee(m as Partial<T.EmployeeInput>, c.country);
  if (Object.keys(fe).length) return { fe };
  const basic = m.basic as number;
  const d = allowancesFor(basic, c.state.settings.allowances);
  const e: T.Employee = {
    id: 0, code: "",
    nameEn: str(m.nameEn), nameAr: str(m.nameAr), nationality: str(m.nationality),
    gender: str(m.gender) as T.Employee["gender"],
    dept: str(m.dept), title: str(m.title), branch: str(m.branch),
    basic, housing: has(m, "housing") ? (m.housing as number) : d.housing, transport: has(m, "transport") ? (m.transport as number) : d.transport,
    other: has(m, "other") ? (m.other as number) : 0,
    joined: str(m.joined), status: (str(m.status) || "Active") as T.EmployeeStatus, idNumber: str(m.idNumber),
    mobile: str(m.mobile), email: str(m.email), bank: str(m.bank), iban: str(m.iban), dob: str(m.dob),
    gosiRegime: (str(m.gosiRegime) || "legacy") as T.Employee["gosiRegime"], gosiWage: 0,
    qiwa: { contractNo: "", status: "Pending", basic },
    disabled: m.disabled === true, probationEnd: (m.probationEnd as string | null) ?? null, cchiClass: str(m.cchiClass),
    dependants: Array.isArray(m.dependants) ? (m.dependants as T.Dependant[]) : [],
    ticket: (m.ticket as T.Employee["ticket"]) ?? null,
    iqamaExpiry: (m.iqamaExpiry as string | null) ?? null,
    documents: Array.isArray(m.documents) ? (m.documents as T.EmpDocument[]) : [],
  };
  e.gosiWage = e.basic + e.housing;
  if (e.nationality !== c.country && e.iqamaExpiry && !e.documents.some((x) => x.kind === "Iqama")) {
    e.documents.push({ kind: "Iqama", number: e.idNumber, expiry: e.iqamaExpiry });
  }
  return { e };
}

function insertEmployee(c: Ctx, e: T.Employee): T.Employee {
  const id = c.nextId("employees", 101, () => c.state.employees.reduce((mx, x) => Math.max(mx, x.id), 0));
  e.id = id;
  e.code = "EMP-" + String(id).padStart(4, "0");
  c.state.employees.push(e);
  const ob = c.state.onboarding.find((x) => x.employeeId === id);
  if (ob) { ob.start = e.joined; ob.tasks = [0, 0, 0, 0, 0, 0, 0, 0]; }
  else c.state.onboarding.push({ employeeId: id, start: e.joined, tasks: [0, 0, 0, 0, 0, 0, 0, 0] });
  return e;
}

const idTaken = (c: Ctx, idNumber: string, exceptId: number) => c.state.employees.some((x) => x.idNumber.trim() === idNumber.trim() && x.id !== exceptId);

async function headBytes(b: Blob, n: number): Promise<Uint8Array> {
  const part = b.slice(0, n);
  if (typeof part.arrayBuffer === "function") return new Uint8Array(await part.arrayBuffer());
  if (typeof FileReader !== "undefined") {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
      r.onerror = () => reject(r.error);
      r.readAsArrayBuffer(part);
    });
  }
  return new Uint8Array();
}

function sniffOK(ext: string, h: Uint8Array): boolean {
  const starts = (sig: number[]) => sig.every((v, i) => h[i] === v);
  const ascii = (from: number, to: number) => String.fromCharCode(...h.slice(from, to));
  switch (ext) {
    case "pdf": return ascii(0, 4) === "%PDF";
    case "png": return starts([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    case "jpg": case "jpeg": return starts([0xff, 0xd8, 0xff]);
    case "webp": return h.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP";
    case "xlsx": return ascii(0, 2) === "PK";
  }
  return true;
}

export function employeesOps(c: Ctx) {
  return {
    async listEmployees(q: T.EmployeeQuery = {}): Promise<T.Page<T.Employee>> {
      c.principal("employees.read");
      const fe: Record<string, string> = {};
      let page = 1, size = 25;
      if (q.page != null) { if (!isInt(q.page) || q.page < 1) fe.page = "must be an integer ≥ 1"; else page = q.page; }
      if (q.pageSize != null) { if (!isInt(q.pageSize) || q.pageSize < 1) fe.pageSize = "must be an integer between 1 and 1000"; else size = Math.min(1000, q.pageSize); }
      const sort = q.sort || "name", dir = q.dir || "asc";
      if (!["name", "basic", "joined", "code", "dept"].includes(sort)) fe.sort = "must be one of name, code, basic, joined, dept";
      if (dir !== "asc" && dir !== "desc") fe.dir = "must be asc or desc";
      if (Object.keys(fe).length) throw E.validation(fe);
      const needle = (q.q || "").trim().toLowerCase();
      const out = c.employeesSorted().filter((e) => {
        if (needle && ![e.nameEn, e.nameAr, e.code, e.idNumber, e.title, e.email].some((x) => (x || "").toLowerCase().includes(needle))) return false;
        if (q.dept && e.dept !== q.dept) return false;
        if (q.branch && e.branch !== q.branch) return false;
        if (q.nat) {
          if (q.nat === "SA") { if (e.nationality !== "SA") return false; }
          else if (q.nat === "non") { if (e.nationality === c.country) return false; }
          else if (e.nationality.toLowerCase() !== q.nat.toLowerCase()) return false;
        }
        if (q.status && e.status !== q.status) return false;
        return true;
      });
      const cmpStr = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
      const cmp = (a: T.Employee, b: T.Employee) => {
        switch (sort) {
          case "basic": return a.basic - b.basic;
          case "joined": return cmpStr(a.joined, b.joined);
          case "code": return cmpStr(a.code, b.code);
          case "dept": return cmpStr(a.dept, b.dept);
        }
        return cmpStr(a.nameEn, b.nameEn);
      };
      out.sort((a, b) => { const r = cmp(a, b); return r !== 0 ? (dir === "desc" ? -r : r) : a.id - b.id; });
      return { items: out.slice((page - 1) * size, page * size), total: out.length, page, pageSize: size };
    },

    async allEmployees(): Promise<T.Employee[]> {
      c.principal("employees.read");
      return c.employeesSorted();
    },

    async getEmployee(id: number): Promise<T.Employee> {
      const u = c.principal("employees.one");
      if (c.isEmployee(u) && c.ownEid(u) !== id) throw E.forbidden("employees may only view their own record");
      return c.emp(id);
    },

    async createEmployee(input: T.EmployeeInput): Promise<T.Employee> {
      const u = c.principal("employees.write");
      const r = buildEmployee(c, input as unknown as Row);
      if ("fe" in r) throw E.validation(r.fe);
      if (idTaken(c, r.e.idNumber, 0)) throw E.conflict("idNumber already used");
      const e = insertEmployee(c, r.e);
      c.audit(u, "Created employee", "Employee", String(e.id), e.nameEn);
      return e;
    },

    async updateEmployee(id: number, patch: Partial<T.EmployeeInput>): Promise<T.Employee> {
      const u = c.principal("employees.write");
      const cur = c.emp(id);
      const merged: Row = { ...cur };
      for (const [k, v] of Object.entries(patch || {})) if (k !== "id" && k !== "code") merged[k] = v;
      if (typeof merged.idNumber === "string") merged.idNumber = merged.idNumber.trim();
      const fe = validateEmployee(merged as Partial<T.EmployeeInput>, c.country);
      if (Object.keys(fe).length) throw E.validation(fe);
      const e = { ...(merged as unknown as T.Employee), id: cur.id, code: cur.code };
      e.documents = e.documents ?? [];
      e.dependants = e.dependants ?? [];
      if (idTaken(c, e.idNumber, id)) throw E.conflict("idNumber already used");
      Object.assign(cur, e);
      c.audit(u, "Updated employee", "Employee", String(id), Object.keys(patch || {}).sort().join(", "));
      return cur;
    },

    async importEmployees(rows: Partial<T.EmployeeInput>[]): Promise<T.ImportResult> {
      const u = c.principal("employees.write");
      if (!Array.isArray(rows)) throw E.field("rows", "required");
      if (rows.length > 5000) throw E.field("rows", "max 5000 rows");
      const ids = new Set(c.state.employees.map((e) => e.idNumber.trim()));
      const res: T.ImportResult = { created: 0, skipped: 0, errors: [] };
      rows.forEach((raw, i) => {
        const row = i + 1;
        if (raw == null || typeof raw !== "object" || Array.isArray(raw)) { res.skipped++; res.errors.push({ row, message: "row: must be an object" }); return; }
        const r = buildEmployee(c, raw as Row);
        if ("fe" in r) { res.skipped++; res.errors.push({ row, message: joinFields(r.fe) }); return; }
        if (ids.has(r.e.idNumber)) { res.skipped++; res.errors.push({ row, message: "duplicate idNumber" }); return; }
        insertEmployee(c, r.e);
        ids.add(r.e.idNumber);
        res.created++;
      });
      c.audit(u, "Imported employees", "Employee", "", `${res.created} created, ${res.skipped} skipped`);
      return res;
    },

    async expiringDocuments(days: number): Promise<T.ExpiringDoc[]> {
      c.principal("employees.read");
      const d = days == null ? 90 : days;
      if (!isInt(d) || d < 0 || d > 100000) throw E.field("days", "must be an integer between 0 and 100000");
      return expiringList(c, d);
    },

    async renewDocument(employeeId: number, kind: string, expiry: string, fileKey?: string): Promise<T.Employee> {
      const u = c.principal("employees.write");
      const e = c.emp(employeeId);
      const idx = e.documents.findIndex((d) => d.kind === kind);
      if (idx < 0) throw E.notFound("document not found");
      if (!isISODate(expiry)) throw E.field("expiry", "valid date required");
      if (expiry <= c.today) throw E.field("expiry", "must be after today");
      if (fileKey && !c.ownsKey(u, fileKey)) throw E.field("fileKey", "invalid file key");
      e.documents[idx].expiry = expiry;
      if (fileKey) e.documents[idx].fileKey = fileKey;
      if (kind === "Iqama") {
        e.iqamaExpiry = expiry;
        for (const d of e.documents) if (d.kind === "Work permit") d.expiry = expiry;
      }
      c.audit(u, "Renewed document", "Employee", String(employeeId), kind + " → " + expiry);
      return e;
    },

    async uploadFile(file: File | Blob, name: string, folder: string): Promise<T.FileRef> {
      const u = c.principal("files");
      if (file == null || typeof (file as Blob).size !== "number") throw E.field("file", "required");
      // Same as HttpProvider: the explicit name is the multipart filename; fall back to File.name.
      const fileName = name || (typeof File !== "undefined" && file instanceof File ? file.name : "");
      if (!FOLDERS.includes(folder)) throw E.field("folder", "must be one of " + FOLDERS.join(", "));
      const dot = (fileName || "").lastIndexOf(".");
      const ext = dot < 0 ? "" : fileName.slice(dot + 1).toLowerCase();
      const contentType = FILE_TYPES[ext];
      if (!contentType) throw E.field("file", "unsupported file type (allowed: pdf, png, jpeg, webp, heic, csv, txt, xlsx, xls)");
      if (file.size > MAX_UPLOAD) throw E.tooLarge("file exceeds 10 MB");
      if (file.size === 0) throw E.field("file", "empty file");
      if (!sniffOK(ext, await headBytes(file, 16))) throw E.field("file", "content does not match the file type");
      const ref = putFile(c, folder, sanitizeName(fileName), contentType, file);
      c.audit(u, "Uploaded file", "File", ref.key, folder);
      return ref;
    },
  };
}
